import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Download, Pencil, Plus, QrCode } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { downloadCsv } from "@/lib/utils";
import { Modal, Field, TextArea, SelectField, AdminButton } from "@/pages/admin/ui";
import { SCHOOLS, STATUS_META, CERT_TYPES } from "@/lib/learning";

const fmt = (d) => new Date(d).toLocaleString("en-IN");
const STATUSES = ["DELIVERED", "OFFERED", "COMING_SOON", "CUSTOM", "FULL", "COMPLETED", "ARCHIVED"];
const toList = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const fromList = (a) => (a || []).join("\n");
const selCls = "border border-ptf-primary/20 bg-white px-2 py-1.5 text-xs outline-none focus:border-ptf-secondary text-ptf-text";

const SectionHead = ({ title, count, onExport, exportTestid, extra }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
    <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">{title} ({count})</h2>
    <div className="flex gap-2">
      {extra}
      {count > 0 && onExport && (
        <button onClick={onExport} data-testid={exportTestid} className="inline-flex items-center gap-2 border border-ptf-primary/20 px-4 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ptf-primary hover:bg-ptf-primary/5 transition-colors">
          <Download className="h-3.5 w-3.5" /> CSV
        </button>
      )}
    </div>
  </div>
);

function ProgrammesSection() {
  const qc = useQueryClient();
  const { data: programmes = [] } = useQuery({ queryKey: ["admin-training"], queryFn: async () => (await api.get("/training")).data });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-training"] }); qc.invalidateQueries({ queryKey: ["training"] }); };
  const patchStatus = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/training/${id}/status`, { status }),
    onSuccess: () => { invalidate(); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const save = useMutation({
    mutationFn: (body) => (editing?.id ? api.put(`/admin/training/${editing.id}`, body) : api.post("/admin/training", body)),
    onSuccess: () => { invalidate(); toast.success("Saved"); setEditing(null); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/admin/training/${id}`),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openEdit = (p) => {
    setEditing(p || {});
    setForm(p ? { ...p, learn_s: fromList(p.learn), who_for_s: fromList(p.who_for), prerequisites_s: fromList(p.prerequisites), safety_s: fromList(p.safety), what_to_bring_s: fromList(p.what_to_bring), upcoming_dates_s: fromList(p.upcoming_dates), country: p.country || "India", currency: p.currency || "INR", featured: !!p.featured, registration_status: p.registration_status || "OPEN", editions_s: (p.editions || []).map((ed) => [ed.start_date, ed.end_date, ed.capacity, ed.seats_booked || 0, ed.registration_status || "OPEN", ed.id].join("|")).join("\n"), related_expeditions_s: (p.related_expedition_ids || []).join(", ") }
      : { title: "", category: SCHOOLS[0].slug, subcategory: "", description: "", about: "", status: "COMING_SOON", format: "FIELD", duration: "", location: "To be announced", level: "Open to beginners", terrain: "", certificate: "DEPENDS", fee_type: "ENQUIRE", fee: 0, instructor: "", age_group: "", learn_s: "", who_for_s: "", prerequisites_s: "", safety_s: "", what_to_bring_s: "", upcoming_dates_s: "", country: "India", currency: "INR", featured: false, registration_status: "OPEN", editions_s: "", related_expeditions_s: "", eligible_for_discount: false });
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    const school = SCHOOLS.find((s) => s.slug === form.category);
    save.mutate({
      ...form, category_name: school?.name || form.category_name || "",
      fee: Number(form.fee) || 0,
      country: form.country || "India", currency: form.currency || "INR",
      featured: !!form.featured, registration_status: form.registration_status || "OPEN",
      learn: toList(form.learn_s), who_for: toList(form.who_for_s), prerequisites: toList(form.prerequisites_s),
      safety: toList(form.safety_s), what_to_bring: toList(form.what_to_bring_s), upcoming_dates: toList(form.upcoming_dates_s),
      editions: toList(form.editions_s).map((line) => {
        const [start_date, end_date, capacity, seats_booked, registration_status, id] = line.split("|").map((s) => (s || "").trim());
        const ed = { start_date, end_date, capacity: Number(capacity) || 0, seats_booked: Number(seats_booked) || 0, registration_status: registration_status || "OPEN" };
        if (id) ed.id = id;
        return ed;
      }),
      related_expedition_ids: (form.related_expeditions_s || "").split(",").map((s) => s.trim()).filter(Boolean),
    });
  };

  const filtered = useMemo(() => programmes.filter((p) =>
    (!cat || p.category === cat) && (!q || `${p.title} ${p.subcategory}`.toLowerCase().includes(q.toLowerCase()))
  ), [programmes, q, cat]);

  return (
    <div>
      <SectionHead
        title="Training Programmes"
        count={filtered.length}
        extra={<AdminButton data-testid="add-programme-btn" onClick={() => openEdit(null)}><Plus className="h-3.5 w-3.5" /> New Programme</AdminButton>}
      />
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="admin-training-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search programmes" className={`${selCls} py-2 flex-1 min-w-[200px]`} />
        <select data-testid="admin-training-category" value={cat} onChange={(e) => setCat(e.target.value)} className={selCls}>
          <option value="">All categories</option>
          {SCHOOLS.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
        </select>
      </div>
      <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1">
        {filtered.map((p) => (
          <div key={p.id} data-testid={`admin-programme-${p.slug}`} className="border border-ptf-primary/10 bg-white px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <p className="font-medium text-ptf-primary text-sm">{p.title}</p>
              <p className="text-[0.6rem] uppercase tracking-[0.12em] text-ptf-text/50">{p.category_name}{p.subcategory ? ` · ${p.subcategory}` : ""}</p>
            </div>
            <select
              data-testid={`status-select-${p.slug}`}
              value={p.status}
              onChange={(e) => patchStatus.mutate({ id: p.id, status: e.target.value })}
              className={selCls}
            >
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
            </select>
            <button onClick={() => openEdit(p)} data-testid={`edit-programme-${p.slug}`} className="grid place-items-center h-8 w-8 border border-ptf-primary/15 hover:bg-ptf-primary/5"><Pencil className="h-3.5 w-3.5 text-ptf-primary" /></button>
            <button onClick={() => { if (window.confirm("Delete this programme?")) remove.mutate(p.id); }} data-testid={`delete-programme-${p.slug}`} className="grid place-items-center h-8 w-8 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit programme" : "New programme"}>
        {form && (
          <form onSubmit={submit} className="space-y-4" data-testid="programme-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title *" required value={form.title} onChange={set("title")} data-testid="pf-title" />
              <SelectField label="Category" value={form.category} onChange={set("category")} options={SCHOOLS.map((s) => s.slug)} data-testid="pf-category" />
              <Field label="Subcategory" value={form.subcategory} onChange={set("subcategory")} data-testid="pf-subcategory" />
              <SelectField label="Status" value={form.status} onChange={set("status")} options={STATUSES} data-testid="pf-status" />
              <Field label="Format" value={form.format} onChange={set("format")} data-testid="pf-format" />
              <Field label="Duration" value={form.duration} onChange={set("duration")} data-testid="pf-duration" />
              <Field label="Location" value={form.location} onChange={set("location")} data-testid="pf-location" />
              <Field label="Level" value={form.level} onChange={set("level")} data-testid="pf-level" />
              <Field label="Age group" value={form.age_group} onChange={set("age_group")} data-testid="pf-age" />
              <SelectField label="Terrain" value={form.terrain} onChange={set("terrain")} options={["", "LAND", "WATER", "MOUNTAIN", "AIR"]} data-testid="pf-terrain" />
              <SelectField label="Certificate" value={form.certificate} onChange={set("certificate")} options={["YES", "NO", "DEPENDS"]} data-testid="pf-certificate" />
              <SelectField label="Fee type" value={form.fee_type} onChange={set("fee_type")} options={["FREE", "DONATION", "PAID", "SPONSORED", "CUSTOM", "ENQUIRE"]} data-testid="pf-feetype" />
              <Field label="Fee (₹, for PAID)" type="number" value={form.fee} onChange={set("fee")} data-testid="pf-fee" />
              <Field label="Instructor / facilitator" value={form.instructor} onChange={set("instructor")} data-testid="pf-instructor" />
              <Field label="Country" value={form.country} onChange={set("country")} data-testid="pf-country" />
              <SelectField label="Currency" value={form.currency} onChange={set("currency")} options={["INR", "USD", "EUR"]} data-testid="pf-currency" />
              <SelectField label="Registration status" value={form.registration_status} onChange={set("registration_status")} options={["OPEN", "CLOSED", "FULL", "ENQUIRE"]} data-testid="pf-regstatus" />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm((prev) => ({ ...prev, featured: e.target.checked }))} data-testid="pf-featured" className="h-4 w-4 accent-ptf-secondary" />
              <span className="text-sm font-medium text-ptf-primary">Featured programme</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!form.eligible_for_discount} onChange={(e) => setForm((prev) => ({ ...prev, eligible_for_discount: e.target.checked }))} data-testid="pf-eligible-discount" className="h-4 w-4 accent-ptf-secondary" />
              <span className="text-sm font-medium text-ptf-primary">Eligible for Membership Discount</span>
            </label>
            <TextArea label="Editions / dates (one per line: start date|end date|capacity|seats booked|OPEN or CLOSED. Leave the last field alone when editing.)" value={form.editions_s} onChange={set("editions_s")} placeholder={"2026-09-12|2026-09-14|20|0|OPEN"} data-testid="pf-editions" />
            <Field label="Related expedition IDs (comma separated)" value={form.related_expeditions_s} onChange={set("related_expeditions_s")} data-testid="pf-related-exp" />
            <TextArea label="Short description" value={form.description} onChange={set("description")} data-testid="pf-description" />
            <TextArea label="About the programme" value={form.about} onChange={set("about")} data-testid="pf-about" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextArea label="What you will learn (one per line)" value={form.learn_s} onChange={set("learn_s")} data-testid="pf-learn" />
              <TextArea label="Who it is for (one per line)" value={form.who_for_s} onChange={set("who_for_s")} data-testid="pf-who" />
              <TextArea label="Prerequisites (one per line)" value={form.prerequisites_s} onChange={set("prerequisites_s")} data-testid="pf-prereq" />
              <TextArea label="Safety requirements (one per line)" value={form.safety_s} onChange={set("safety_s")} data-testid="pf-safety" />
              <TextArea label="What to bring (one per line)" value={form.what_to_bring_s} onChange={set("what_to_bring_s")} data-testid="pf-bring" />
              <TextArea label="Upcoming dates (one per line)" value={form.upcoming_dates_s} onChange={set("upcoming_dates_s")} data-testid="pf-dates" />
            </div>
            <AdminButton type="submit" data-testid="pf-save">Save Programme</AdminButton>
          </form>
        )}
      </Modal>
    </div>
  );
}

function RegistrationsSection() {
  const qc = useQueryClient();
  const { data: regs = [] } = useQuery({ queryKey: ["admin-registrations"], queryFn: async () => (await api.get("/admin/training-registrations")).data });
  const patch = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/training-registrations/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-registrations"] }); toast.success("Status updated (email sent where applicable)"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/training-registrations/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-registrations"] }); toast.success("Deleted"); },
  });
  const exportCsv = () => downloadCsv(
    [["programme", "name", "email", "phone", "country", "city", "age", "preferred_date", "experience_level", "status", "volunteer_interest", "created_at"],
      ...regs.map((r) => [r.programme_title, r.name, r.email, r.phone, r.country, r.city, r.age, r.preferred_date, r.experience_level, r.status, r.volunteer_interest ? "yes" : "no", r.created_at])],
    "training-registrations.csv"
  );
  return (
    <div className="mt-14">
      <SectionHead title="Registrations" count={regs.length} onExport={exportCsv} exportTestid="export-registrations-csv" />
      <div className="space-y-2">
        {regs.length === 0 && <p className="text-sm text-ptf-text/50">No registrations yet.</p>}
        {regs.map((r) => (
          <div key={r.id} data-testid="registration-row" className="border border-ptf-primary/10 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-ptf-primary text-sm">{r.name} · {r.programme_title || r.programme_slug}</p>
              <span className="flex items-center gap-2">
                <select value={r.status} onChange={(e) => patch.mutate({ id: r.id, status: e.target.value })} data-testid="registration-status-select" className={selCls}>
                  {["received", "confirmed", "waitlisted", "cancelled"].map((s) => <option key={s}>{s}</option>)}
                </select>
                <span className="text-xs text-ptf-text/45">{fmt(r.created_at)}</span>
                <button onClick={() => { if (window.confirm("Delete this registration?")) del.mutate(r.id); }} data-testid="delete-registration" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-xs text-ptf-secondary mt-1">{r.email}{r.phone ? ` · ${r.phone}` : ""}{r.city ? ` · ${r.city}` : ""}{r.volunteer_interest ? " · interested in volunteering" : ""}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function VolunteerApplicationsSection() {
  const qc = useQueryClient();
  const { data: apps = [] } = useQuery({ queryKey: ["admin-vol-apps"], queryFn: async () => (await api.get("/admin/volunteer-applications")).data });
  const patch = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/volunteer-applications/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-vol-apps"] }); toast.success("Status updated (email sent where applicable)"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/volunteer-applications/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-vol-apps"] }); toast.success("Deleted"); },
  });
  const exportCsv = () => downloadCsv(
    [["name", "email", "phone", "city", "country", "areas", "commitment", "availability", "skills", "status", "created_at"],
      ...apps.map((a) => [a.name, a.email, a.phone, a.city, a.country, (a.areas || []).join("; "), a.commitment, a.availability, a.skills, a.status, a.created_at])],
    "volunteer-applications.csv"
  );
  return (
    <div className="mt-14">
      <SectionHead title="Volunteer Applications" count={apps.length} onExport={exportCsv} exportTestid="export-vol-apps-csv" />
      <div className="space-y-2">
        {apps.length === 0 && <p className="text-sm text-ptf-text/50">No applications yet.</p>}
        {apps.map((a) => (
          <div key={a.id} data-testid="vol-app-row" className="border border-ptf-primary/10 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-ptf-primary text-sm">{a.name}{a.commitment ? ` · ${a.commitment}` : ""}</p>
              <span className="flex items-center gap-2">
                <select value={a.status} onChange={(e) => patch.mutate({ id: a.id, status: e.target.value })} data-testid="vol-app-status-select" className={selCls}>
                  {["received", "screening", "accepted", "declined"].map((s) => <option key={s}>{s}</option>)}
                </select>
                <span className="text-xs text-ptf-text/45">{fmt(a.created_at)}</span>
                <button onClick={() => { if (window.confirm("Delete this application?")) del.mutate(a.id); }} data-testid="delete-vol-app" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-xs text-ptf-secondary mt-1">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
            <p className="text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/55 mt-1">{(a.areas || []).join(" · ")}</p>
            {a.motivation && <p className="text-xs text-ptf-text/70 mt-1.5">{a.motivation}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupEnquiriesSection() {
  const qc = useQueryClient();
  const { data: enquiries = [] } = useQuery({ queryKey: ["admin-group-enquiries"], queryFn: async () => (await api.get("/admin/group-enquiries")).data });
  const patch = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/group-enquiries/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-group-enquiries"] }); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/group-enquiries/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-group-enquiries"] }); toast.success("Deleted"); },
  });
  return (
    <div className="mt-14">
      <SectionHead title="Group / Institutional Enquiries" count={enquiries.length} />
      <div className="space-y-2">
        {enquiries.length === 0 && <p className="text-sm text-ptf-text/50">No group enquiries yet.</p>}
        {enquiries.map((g) => (
          <div key={g.id} data-testid="group-enquiry-row" className="border border-ptf-primary/10 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-ptf-primary text-sm">{g.organisation} ({g.org_type || "organisation"}) · {g.name}</p>
              <span className="flex items-center gap-2">
                <select value={g.status} onChange={(e) => patch.mutate({ id: g.id, status: e.target.value })} data-testid="group-enquiry-status-select" className={selCls}>
                  {["received", "in discussion", "proposed", "delivered", "closed"].map((s) => <option key={s}>{s}</option>)}
                </select>
                <span className="text-xs text-ptf-text/45">{fmt(g.created_at)}</span>
                <button onClick={() => { if (window.confirm("Delete this enquiry?")) del.mutate(g.id); }} data-testid="delete-group-enquiry" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-xs text-ptf-secondary mt-1">{g.email}{g.phone ? ` · ${g.phone}` : ""}{g.participants ? ` · ~${g.participants} participants` : ""}</p>
            <p className="text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/55 mt-1">{(g.programme_interest || []).join(" · ")}</p>
            {g.message && <p className="text-xs text-ptf-text/70 mt-1.5">{g.message}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function CertificatesSection() {
  const qc = useQueryClient();
  const { data: certs = [] } = useQuery({ queryKey: ["admin-certificates"], queryFn: async () => (await api.get("/admin/certificates")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", programme: "", certificate_type: CERT_TYPES[0], issue_date: "", duration: "", location: "", signatory: "Founder, Planet Trotter Foundation" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const issue = useMutation({
    mutationFn: (body) => api.post("/admin/certificates", body),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["admin-certificates"] }); toast.success(`Issued ${res.data.cert_number}`); setOpen(false); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const patch = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/certificates/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-certificates"] }); toast.success("Updated"); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/certificates/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-certificates"] }); toast.success("Deleted"); },
  });
  const exportCsv = () => downloadCsv(
    [["cert_number", "name", "email", "programme", "type", "issue_date", "status"],
      ...certs.map((c) => [c.cert_number, c.name, c.email, c.programme, c.certificate_type, c.issue_date, c.status])],
    "certificates.csv"
  );
  const verifyUrl = (n) => `${window.location.origin}/verify?c=${n}`;

  return (
    <div className="mt-14">
      <SectionHead
        title="Certificates"
        count={certs.length}
        onExport={exportCsv}
        exportTestid="export-certificates-csv"
        extra={<AdminButton data-testid="issue-certificate-btn" onClick={() => setOpen(true)}><Plus className="h-3.5 w-3.5" /> Issue Certificate</AdminButton>}
      />
      <div className="space-y-2">
        {certs.length === 0 && <p className="text-sm text-ptf-text/50">No certificates issued yet.</p>}
        {certs.map((c) => (
          <div key={c.id} data-testid="certificate-row" className="border border-ptf-primary/10 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-ptf-primary text-sm">{c.cert_number} · {c.name} · {c.programme}</p>
              <span className="flex items-center gap-2">
                <span className={`text-[0.6rem] uppercase tracking-[0.12em] px-2 py-1 ${c.status === "VALID" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{c.status}</span>
                <button
                  data-testid="cert-toggle-status"
                  onClick={() => patch.mutate({ id: c.id, status: c.status === "VALID" ? "REVOKED" : "VALID" })}
                  className="border border-ptf-primary/15 px-2.5 py-1 text-[0.6rem] uppercase tracking-[0.12em] text-ptf-primary hover:bg-ptf-primary/5"
                >
                  {c.status === "VALID" ? "Revoke" : "Restore"}
                </button>
                <button onClick={() => { if (window.confirm("Delete this certificate record?")) del.mutate(c.id); }} data-testid="delete-certificate" className="grid place-items-center h-7 w-7 border border-ptf-primary/15 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
              </span>
            </div>
            <p className="text-xs text-ptf-text/55 mt-1">{c.certificate_type} · {c.issue_date}{c.location ? ` · ${c.location}` : ""}</p>
            <div className="mt-2 flex items-center gap-3">
              <a href={verifyUrl(c.cert_number)} target="_blank" rel="noreferrer" data-testid="cert-verify-link" className="text-xs underline underline-offset-2 text-ptf-secondary">{verifyUrl(c.cert_number)}</a>
              <a href={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(verifyUrl(c.cert_number))}`} target="_blank" rel="noreferrer" data-testid="cert-qr-link" className="inline-flex items-center gap-1 text-xs text-ptf-primary hover:text-ptf-secondary">
                <QrCode className="h-3.5 w-3.5" /> QR
              </a>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Issue certificate">
        <form onSubmit={(e) => { e.preventDefault(); issue.mutate(form); }} className="space-y-4" data-testid="certificate-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Participant name *" required value={form.name} onChange={set("name")} data-testid="cf-name" />
            <Field label="Email (optional, sends notification)" type="email" value={form.email} onChange={set("email")} data-testid="cf-email" />
            <Field label="Programme *" required value={form.programme} onChange={set("programme")} data-testid="cf-programme" />
            <SelectField label="Certificate type" value={form.certificate_type} onChange={set("certificate_type")} options={CERT_TYPES} data-testid="cf-type" />
            <Field label="Issue date (blank = today)" value={form.issue_date} onChange={set("issue_date")} data-testid="cf-date" />
            <Field label="Programme duration" value={form.duration} onChange={set("duration")} data-testid="cf-duration" />
            <Field label="Location" value={form.location} onChange={set("location")} data-testid="cf-location" />
            <Field label="Authorised signatory" value={form.signatory} onChange={set("signatory")} data-testid="cf-signatory" />
          </div>
          <AdminButton type="submit" data-testid="cf-issue">Issue Certificate</AdminButton>
        </form>
      </Modal>
    </div>
  );
}

export default function LearningAdmin() {
  return (
    <div>
      <ProgrammesSection />
      <RegistrationsSection />
      <VolunteerApplicationsSection />
      <GroupEnquiriesSection />
      <CertificatesSection />
    </div>
  );
}
